import styled from "styled-components";
import BaseButton from "components/BaseButton";
import {pillShape} from "components/BaseButton.styles";
import {LogoMark} from "nav/BrandMark";
import {CREATE_CARD_BLOCK_PADDING, CREATE_CARD_INLINE_PADDING} from "home/CreateFlow.styles";
import {fontFamilies, radii} from "theme/tokens";
import {displayAccentCss, displaySerifCss} from "theme/typography";

/**
 * The one distance this surface stacks things by: mark-to-heading across {@link Header}'s row, and
 * heading-to-notice-to-button down {@link Root}'s column.
 *
 * Named rather than written out three times, because the claim the design makes is that the row and
 * the column share a rhythm — a claim that only holds while the three numbers are the same one, and
 * prose saying so cannot keep them that way.
 */
const STACK_GAP = "14px";

/**
 * The consent gate's surface (ui#1170, resized in ui#1549, floated in ui#1575, centred in ui#1713,
 * a full card TAKEOVER since ui#1728, with its mark and heading on one row since ui#1752, and its
 * notice tucked under that row at reading size since ui#1757).
 *
 * Where it SITS is {@link home/CreateFlow.styles.ConsentDock}'s job — this file owns what the gate
 * looks like and how its children stack.
 *
 * <b>That split used to mean this surface was host-agnostic, and since ui#1728 it honestly is not.</b>
 * A floating panel could have been raised anywhere; a TAKEOVER is defined by the box it takes over. So
 * this file now reads its host's geometry on purpose — the corner, inherited from the dock, and the
 * card's own {@link CREATE_CARD_INLINE_PADDING} and {@link CREATE_CARD_BLOCK_PADDING} — rather than
 * restating any of it as numbers that look right today.
 * The gate has exactly one host and always has; the previous wording described a generality it was
 * never asked to have, and pretending to it here would mean two copies of the card's geometry.
 *
 * <b>Theme-aware, where every earlier version was deliberately mode-independent.</b> ui#1170 through
 * ui#1713 painted brand navy with white ink in light and dark alike, so the gate read as one fixed
 * surface floating on the card. The owner-approved ui#1728 design inverts that premise: the gate is
 * not ON the card any more, it IS the card until the visitor agrees — so it has to be the card's own
 * paint, which means `theme.tokens` and only `theme.tokens`. A navy plate inside a dark card would be
 * the one surface on the page that ignores the mode.
 *
 * That single change cascades through everything else in this file, and the cascade is the reason the
 * whole surface was reworked rather than repainted: on a navy plate white was the only ink that
 * worked, so the notice, the links and BOTH focus rings were white literals. On a surface that is
 * white in light mode every one of them would have been invisible. They are re-derived from tokens
 * below, each with its own note.
 */

/**
 * The takeover itself: the card's own paint, filling the frame {@link ConsentDock} gives it.
 *
 * OPAQUE is the load-carrying property, not a stylistic one — it is what hides the drop hero while the
 * gate is up, which the owner approved as the point of the design. The hero is COVERED, never hidden:
 * it keeps its real box and its real size in both states (`home-hero-editorial.spec.ts` checks the hero
 * stays visible under the gate, which `display: none` beneath would fail). Removing the box is the ui#1549
 * defect; painting over it is this ticket.
 *
 * No shadow and no radius of its own. Both existed to make a panel read as RAISED above the card, and
 * there is no longer anything to be raised above — the corner comes from the dock (`inherit`), which
 * computes the card's inner arc once so this file cannot drift from it.
 *
 * LEFT-aligned, where every previous version centred its text. Centring is what suits a short floating
 * notice; a full card of copy reads down a single left edge, and that edge is the card's own — hence
 * the inline padding restated from {@link CREATE_CARD_INLINE_PADDING} rather than a number picked to
 * look close. The gate's text column therefore starts exactly where the sender panel's does, so
 * agreeing does not slide the page's left margin sideways.
 *
 * That edge is the SURFACE's, and since ui#1752 the heading no longer sits on it — the mark does, with
 * the heading indented one tile-and-gap beside it ({@link Header}). The notice and the accept control
 * still begin on it, which is the run of copy the claim above is about.
 */
export const Root = styled.section`
	display: flex;
	flex-direction: column;
	box-sizing: border-box;
	width: 100%;
	min-width: 0;
	/* The MINIMUM between the three blocks. Whatever the card has left over is added below the notice
	   alone (see {@link Copy}), so this is the gap the header keeps from the notice at every viewport,
	   and the floor the notice keeps from the accept control at the tightest one. */
	gap: ${STACK_GAP};
	/* Top is roomier than the card's own: the logo tile needs air above it to read as a mark rather
	   than as content pushed against the edge. Bottom is the card's own value, read from the card
	   rather than restated, so the pill sits exactly one card-padding above the surface's lower edge -
	   which is the distance the 320x568 cookie-banner clearance is a function of. */
	padding: 26px ${CREATE_CARD_INLINE_PADDING} ${CREATE_CARD_BLOCK_PADDING};
	border-radius: inherit;
	background: ${({theme}) => theme.tokens.card};
	color: ${({theme}) => theme.tokens.ink};
	font-family: ${fontFamilies.sans};
	overflow-wrap: anywhere;
`;

/**
 * The Transferify mark, at the top of the takeover.
 *
 * {@link nav/BrandMark.LogoMark} rather than a second tile: it is the repo's one canonical mark
 * (ticket #28 extracted it from the NavBar precisely so no surface paints its own), and it already
 * carries the blue tile, the white glyph, the display-serif face and the token ring shadow. Only the
 * SCALE is this surface's decision — the nav wears it at 30px in a 56px bar, and the approved design
 * asks for roughly half again that here, where it is the first thing on an otherwise empty card.
 *
 * `radii.panel` for the corner rather than a number: at this size the tile is a panel-sized box, and it
 * is the same token the drop hero and the payload box wear, so the mark belongs to the same family of
 * boxes as everything else the card shows.
 *
 * The optical correction is re-picked, not scaled. `LogoMark`'s 3px nudge exists because the serif "T"
 * sits high in its em box, so flex centring leaves it looking lifted; the amount that corrects for is a
 * function of the GLYPH, which grows 22px to 26px here while the tile grows 30px to 44px, so neither
 * ratio yields the other. 4px is what reads level at this size on screen.
 *
 * Decorative and `aria-hidden` at the call site — the gate is already named by its heading.
 */
export const BrandTile = styled(LogoMark)`
	/* Doubled for the same reason {@link AgreeButton} is: these five override LogoMark's own
	   single-class rules from a different module, so at equal specificity the winner would otherwise be
	   whichever sheet styled-components inserted last - a tile that changes size when an import moves. */
	&& {
		width: 44px;
		height: 44px;
		border-radius: ${radii.panel};
		font-size: 26px;
		padding-top: 4px;
		/* The mark is a fixed square and the heading beside it takes the rest of the row. Without this
		   the tile is a flex item at the default shrink factor of 1, so the longest title (Romanian at
		   320px) buys its own width by squashing the mark into a rectangle.
		   (No backticks in a comment inside a styled-components literal - one would end the literal.) */
		flex: none;
	}
`;

/**
 * The mark and the heading, on ONE row (ui#1752).
 *
 * Owner, 2026-08-31: "let's move 'Before you send' next to the logo". They were two stacked rows from
 * ui#1728 until now.
 *
 * <b>Side by side buys vertical room rather than spending it, which is what makes this safe at 320px.</b>
 * Stacked, the two cost the tile's 44px plus the heading's 16px gap plus every line the heading sets;
 * on one row they cost whichever of the two is taller. The heading's column is narrower here by the
 * tile and this gap, so the longest title (Romanian) may take one line more than it did — and even
 * three lines of it measure less than the stack did at two. That matters because the card cannot grow:
 * `ConsentDock`'s single auto grid row pushes the gate and the accept control off the card's surface
 * when the content outgrows it. The tightest case is Romanian at 320px, with 30px of the card still
 * unspent (below); no browser test has read that since kitecorp/transferify-ui#3850.
 *
 * `center` rather than a baseline: the tile is itself a flex box centring a glyph, so its baseline is
 * the "T"'s, already displaced by the tile's own optical `padding-top` — aligning to it would compound
 * two corrections into a number nobody could derive again. Centring the square against the heading's
 * block is stable at one line and at three.
 */
export const Header = styled.div`
	display: flex;
	align-items: center;
	/* Enough air that the mark reads as a mark rather than a dropped capital, and narrow enough to
	   leave the heading its column at 320px. The surface's own {@link STACK_GAP}, so this row and the
	   column it sits in are spaced by one rhythm instead of two. */
	gap: ${STACK_GAP};
	min-width: 0;
`;

/**
 * The gate's heading — the display serif, at the size a takeover can afford.
 *
 * <b>`displaySerifCss` rather than the `DisplaySerif` component.</b> That component is a `styled.h1`
 * and the landing page already has its own `<h1>` ("Send anything."), so rendering it here would put a
 * second top-level heading on the page. The fragment carries the face without the element; the element
 * stays the `<h2>` under the same `id`, so the gate's `aria-labelledby` names the region exactly as it
 * has since ui#1575 — that wiring resolves by id and never depended on the heading's looks.
 *
 * <b>The size is re-derived, and the colour is no longer stated.</b> The fragment's own ramp —
 * `clamp(46px, 5.7vw, 74px)` — is drawn for the landing hero's 430px column and sets three lines inside
 * this card. ui#1713 pinned 26px because a 242px panel had room for one line and no more. The takeover
 * has the whole card, so the ramp below is the approved design's 34px, floored at 28px for the 320px
 * viewport where the Romanian title is longest — measured there rather than assumed, and the floor is
 * what keeps that title on two lines instead of three. The `color` the fragment brings is
 * `theme.tokens.ink`, which is the page's own body ink and is now exactly right: ui#1713 had to
 * override it only because the surface underneath was permanently navy.
 *
 * The italic word is the catalog's, never this file's — see `i18n/richText.en.tsx`'s `consentTitle`
 * for why the accent moves between locales (English accents "you", Romanian the verb "trimite"). `em`
 * is styled here because a reset elsewhere must not be able to quietly flatten the one piece of
 * typography the design is built around.
 *
 * <b>That accent is the landing hero's accent, by the same rule rather than a copy of it</b> (ui#1752;
 * owner, 2026-08-31: "make 'you' primary color keep italic"). {@link theme/typography.displayAccentCss}
 * is what {@link theme/typography.DisplayAccent} paints "Instantly." with, so the two accents on the
 * page cannot drift apart, and the colour is `theme.tokens.blue` — which is a per-mode token, so the
 * dark card gets the lighter blue that reads on it rather than the light mode's ink-dark one.
 *
 * <b>No top margin any more.</b> The 16px here was the gap BELOW the mark while the two were stacked;
 * side by side ({@link Header}) that distance is the row's `gap`, and a stale margin would push the
 * heading down against a tile it is supposed to sit level with. `min-width: 0` lets the heading wrap
 * inside its own column instead of forcing the row wider than the card — the default `min-width: auto`
 * on a flex item floors at its longest word, which is how a takeover starts overflowing the card.
 */
export const Title = styled.h2`
	${displaySerifCss};
	font-size: clamp(28px, 8.6vw, 34px);
	line-height: 1.08;
	letter-spacing: 0.01em;
	margin: 0;
	min-width: 0;

	em {
		${displayAccentCss};
	}
`;

/**
 * The notice: both sentences and their three policy links, as one wrapped block.
 *
 * <b>`margin-bottom: auto` is the whole of the design's spacer, and it points DOWN (ui#1757).</b> It
 * was `margin-top: auto` from ui#1728 until now, which parked the notice at the foot of the card with
 * the accept control and left the whole of the card's spare height as a hole under the heading. Owner,
 * 2026-08-31: "the text can also be moved more under the first row. and font can be bigger." So the
 * spare height moves to the other side of this block: the notice now follows the heading at
 * {@link STACK_GAP}, and what the card has left over falls between the notice and the control, which
 * stays pinned to the bottom. An empty spacer `<div>` would do the same for a reader with eyes and add
 * a meaningless node for everyone else.
 *
 * <b>16px, where every version since ui#1170 set 14px</b> (same instruction). This is the only prose on
 * the surface and the card is entirely its own, so the size that was right for a 60px bar squeezed
 * under the drop hero is smaller than a takeover needs; 16px is the body size the rest of the page
 * reads at.
 *
 * What it costs, measured on the built bundle at both phone widths rather than estimated: the notice
 * takes one line more in Romanian at 375px and in English at 320px (two lines to three), and Romanian
 * at 320px — the longest text in the smallest frame, and the case this surface is always tightest in —
 * stays at three. The block grows 42px to 48px or 63px to 72px accordingly, and the accept control does
 * not move, because every one of those lines is paid for out of the spare height the `auto` below now
 * holds: 30px of it is still unspent in the tightest case, which is the margin a future size increase
 * has to fit inside. No browser test has read it since kitecorp/transferify-ui#3850.
 *
 * `ink2` is the token layer's muted ink and replaces ui#1575's `premInk2`, which is a fixed
 * white-at-85% meant for dark surfaces — correct on navy, invisible on a white card. The links inside
 * stay FULL `ink`, which is what keeps them separable from the sentence around them at this size.
 */
export const Copy = styled.p`
	min-width: 0;
	margin: 0 0 auto;
	font-size: 16px;
	line-height: 1.5;
	color: ${({theme}) => theme.tokens.ink2};
`;

/**
 * A policy link is a real `<button>`, not an anchor.
 *
 * It opens {@link legal/PolicyDialog} rather than navigating, so there is no href to give it — and
 * an `<a>` without one is not focusable and cannot be activated from the keyboard, which is how these
 * three links behaved for their whole life. As a button the browser gives them tab order, Enter/Space
 * and the right role for free; the reset below is what keeps them LOOKING like links.
 *
 * The ink and the ring are both re-derived for the theme-aware surface (ui#1728). `brand.white` was
 * correct on navy and would be a link you cannot see on a white card; `theme.tokens.ink` reads on the
 * card in both modes, and `theme.tokens.blue` is the ring every other control in the repo wears — see
 * {@link components/BaseButton.styles}, whose shared `control` rule this now matches instead of
 * contradicting.
 */
export const PolicyLink = styled.button`
	display: inline;
	/* A taller hit area than the text line, without a taller line: vertical padding on an inline box
	   does not enter the line height, and the negative margin cancels the horizontal growth, so the
	   sentence sets exactly as it did. Measured on the built bundle, the three links are a 24px inline
	   box at the notice's 16px (it was 21px at 14px, before ui#1757), which lands exactly ON WCAG
	   2.5.8's 24px minimum rather than clear of it - and a target at the floor is still the floor. These
	   ten pixels take it to 34px, which is a link a thumb hits. The standard would also exempt a target
	   inline in a sentence, but the exemption is a reason not to break the sentence, not a reason to
	   leave the target small. */
	padding: 5px 3px;
	margin: -5px -3px;
	border: 0;
	background: none;
	font: inherit;
	color: ${({theme}) => theme.tokens.ink};
	text-decoration: underline;
	text-underline-offset: 2px;
	cursor: pointer;

	&:focus-visible {
		outline: 2px solid ${({theme}) => theme.tokens.focusOutline};
		outline-offset: 2px;
	}
`;

/**
 * The one control on this surface, and the one thing between a visitor and the product.
 *
 * It is {@link components/BaseButton}'s `primary` role rather than a hand-rolled `styled(Button)`,
 * which is that primitive's own adoption rule — a surface converts when it is next substantially
 * reworked — and it is what brings the 44px {@link theme/tokens.ACTION_CONTROL_HEIGHT_PX} target, the
 * shared disabled handling and `theme.tokens.blue`/`blueInk` instead of six more lines restating them.
 * Owner, 2026-08-30: "we need primary color for button"; the primitive IS that colour, so nothing here
 * repaints the fill. Full width because the surface has one action and no second half to share a row.
 *
 * <b>Only the corner is overridden, and ui#1713's focus-ring override is DELETED rather than
 * adjusted.</b> That override painted the ring `brand.white`, because `PrimaryButton`'s own
 * `theme.tokens.blue` ring measured 2.82:1 on the navy plate — a ring nobody could see. Both halves of
 * that problem are gone with the plate: on the card, blue measures well past WCAG 1.4.11's 3:1 in both
 * modes, and white would be the invisible one in light. Removing the wrapper's rule hands the ring back
 * to the primitive, which is where it should have been able to live all along.
 *
 * The same repaint retires ui#1713's recorded contrast deviation with it. The primary fill against the
 * navy plate fell under 3:1 for the control's own boundary in light mode; against `theme.tokens.card`
 * it is the ordinary primary-on-card pairing the rest of the app uses, so there is no longer a
 * deviation here to record or to carry forward.
 */
export const AgreeButton = styled(BaseButton)`
	/* The primitive's own pill affordance, shared with ui#1920's progress-card button rather than
	   restated here. Its note carries why the rule is doubled. */
	${pillShape};
`;
